import { render, type JSXChild } from 'engine/index';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import type { VingerAfdrukkenPlayerState, VingerAfdrukkenState } from 'shared/modelTypes/vinger-afdrukken';
import { CheckboxField, ToggleField } from './FormComponents';

export function PlayVingerAfdrukken({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as VingerAfdrukkenState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: VingerAfdrukkenState) => {
		const s = state.stage;

		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={() => {
							console.log('start the game');
							rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'start-game' } });
						}}
						state={state}
					/>,
				);
				break;
			case 'answer-questions':
				{
					const cols = new Map();
					cols.set('Vragen Beantwoord', (p: VingerAfdrukkenPlayerState) => `${p.answers.filter((a) => a.answer).length} / 9`);

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen aan het beantwoorden</h2>
							<Button
								on:click={() => {
									rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'end-questions' } });
								}}
							>
								Beëindigen
							</Button>

							<PlayerTable state={state} sessionId={sessionId} cols={cols} />
						</div>,
					);
				}
				break;
			case 'wait-for-rounds':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen beantwoord</h2>
							<p>Geen uitleg en druk op de knop om de rondes te beginnen.</p>
							<Button
								on:click={() => {
									rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'start-rounds' } });
								}}
							>
								Begin Rondes
							</Button>

							<PlayerTable state={state} />
						</div>,
					);
				}
				break;
			case 'play-rounds':
				{
					const player = state.players[state.currentPlayerIndex];
					const playerScreen = player.lastScreen.kind;

					const cols = new Map();
					cols.set(
						'Vragen gepresenteerd',
						(p: VingerAfdrukkenPlayerState) =>
							`${state.answersOnBoard.filter((a) => a.playerIndex == state.players.findIndex((p2) => p2 == p)).length} / 9`,
					);

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Ronde {state.roundIndex + 1}</h2>
							{playerScreen == 'show-choose-diceroll-screen' && (
								<div>
									<p>{player.name} is aan het dobbelen.</p>
								</div>
							)}
							{playerScreen == 'show-choose-question-screen' && (
								<div>
									<p>{player.name} is een antwoord aan het kiezen.</p>
								</div>
							)}
							{playerScreen == 'show-present-answer-screen' && (
								<div>
									<p>{player.name} is een antwoord aan het presenteren. Druk op de knop om door te gaan.</p>
									<Button
										on:click={() =>
											rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'finish-presenting' } })
										}
									>
										Volgende ronde
									</Button>
								</div>
							)}
							<div class="size-8"></div>
							<ToggleField
								compact
								name="lastRound"
								label="Laatste ronde"
								value={state.lastRound}
								onChange={(v) => {
									const lastRound = v || false;
									rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'set-is-last-round', lastRound } });
								}}
							/>
							<div class="size-8"></div>
							<PlayerTable state={state} sessionId={sessionId} cols={cols} />
						</div>,
					);
				}
				break;
			case 'wait-for-swipe':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Klaar voor het swipen</h2>
							<p>Leg de spelers uit hoe het swipen werkt en druk daarna op de knop om te beginnen.</p>
							<Button
								on:click={() => {
									rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'start-swipe-cards' } });
								}}
							>
								Start Swipen
							</Button>

							<PlayerTable state={state} />
						</div>,
					);
				}
				break;
			case 'play-swipe-card':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Onderwerpen worden gekozen</h2>
							<p>Spelers zijn aan het swipen. Druk op de knop als iedereen klaar is.</p>
							<Button
								on:click={() => {
									rpc.sendVingerAfdrukkenMessage.query({ sessionId, update: { kind: 'start-ending-report' } });
								}}
							>
								Spel beëindigen
							</Button>

							{/* <PlayerTable state={state} /> */}
						</div>,
					);
				}
				break;

			case 'ending-report':
				{
					const cols = new Map();
					cols.set('Vragen', (p: VingerAfdrukkenPlayerState) =>
						p.answers.map((a) => (
							<div>
								<strong>Q {a.questionNumber}:</strong>
								<span>{a.answer}</span>
							</div>
						)),
					);

					render(
						view,
						<div>
							<p>Showing ending report</p>
							<PlayerTable state={state} cols={cols} />
						</div>,
					);
				}
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });
		console.log(state);
		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: VingerAfdrukkenState; onContinue: () => void }) {
	console.log('render players', state.players);

	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			<Button on:click={onContinue}>Doorgaan</Button>
		</div>
	);
}

function PlayerTable({
	state,
	sessionId,
	cols,
}: {
	state: VingerAfdrukkenState;
	sessionId?: string;
	cols?: Map<string, (p: VingerAfdrukkenPlayerState) => JSXChild>;
}) {
	return (
		<Table>
			<thead>
				<TrHead>
					{sessionId && <Th title="When disabled the players turn will always be skipped">Active</Th>}
					<Th>Name</Th>
					<Th>Color</Th>
					<Th>Punten</Th>
					{/* <Th>Antwoord</Th> */}
					{cols && [...cols.entries()].map(([k, _]) => <Th>{k}</Th>)}
					<Th>id</Th>
				</TrHead>
			</thead>
			<tbody>
				{state.players.map((player) => {
					return (
						<TrBody>
							{sessionId && (
								<Td>
									<CheckboxField
										name={player.id + '-active'}
										label={''}
										value={player.active}
										onChange={(v) => {
											rpc.sendFlikkenMessage.query({
												sessionId,
												update: { kind: 'set-player-active', playerId: player.id, active: v ?? true },
											});
										}}
									/>
								</Td>
							)}
							<Td>{player.name}</Td>
							<Td>{player.color}</Td>
							<Td>{player.score}</Td>
							{cols && [...cols.entries()].map(([_, v]) => <Td>{v(player)}</Td>)}
							{/* <Td>{player.roundAnswers[state.roundIndex]?.emotion ?? ''}</Td> */}
							<Td>{player.id}</Td>
						</TrBody>
					);
				})}
			</tbody>
		</Table>
	);
}
